export interface OperatorRegexpCaptureGroups {
    backOp?: string
    backOperator?: string
    backOperatorValue?: string
    colon?: string
    equals?: string
    equalsValue?: string
    frontOp?: string
    frontOperator?: string
    frontOperatorValue?: string
    key?: string
    keyWithColon?: string
    range?: string
    rangeHighValue?: string
    rangeLowValue?: string
    value?: string
    alphanum?: string
    alphanumValue?: string
    quoted?: string
    quotedValue?: string
    comma?: string
    commaValue?: string
}

export const EMPTY_STRING = '^$'

// Primitives
const ALPHANUMERIC_DASH = '[A-Z0-9\\-]+'
const ANY_NON_SPACE_OR_COLON = '[^:\\s]+'
const BACK_OPERATOR = '[+|\\-]'
const COLON = ':'
const COMMA_SEPARATED = `((${ALPHANUMERIC_DASH})(,\\s?${ALPHANUMERIC_DASH})+)`
const FRONT_OPERATOR = '[<>]'
const KEY = '[a-z]+'
const LEADING_SPACE = '\\s|^'
const LEADING_SPACE_NON_CAPTURE = `(?:${LEADING_SPACE})`
const NUMBERS = '\\d+'
const QUOTED = '"[^"]+"'
const SPACE_OR_END = '\\s|$'
const SPACE_OR_END_POS_LOOKAHEAD = `(?=${SPACE_OR_END})`
const YEAR = '(19|20)\\d\\d'

// Named Groups
const KEY_WITH_COLON = `(?<keyWithColon>(?<key>${KEY})(?<colon>${COLON}))`
const NO_KEY_WITH_COLON = `${KEY_WITH_COLON}{0}`

const ANY_VALUE = `(?<anyValue>${ANY_NON_SPACE_OR_COLON})`
const ANY_PARTIAL = `(?<any>${ANY_VALUE}${SPACE_OR_END_POS_LOOKAHEAD})`

const YEAR_EQUALS_VALUE = `(?<equalsValue>${YEAR})`
const YEAR_RANGE_VALUE = `(?<rangeLowValue>${YEAR})-(?<rangeHighValue>${YEAR})`
const YEAR_FRONT_OPERATOR_VALUE = `(?<frontOperator>${FRONT_OPERATOR})(?<frontOperatorValue>${YEAR})`
const YEAR_BACK_OPERATOR_VALUE = `(?<backOperatorValue>${YEAR})(?<backOperator>${BACK_OPERATOR})`
const YEAR_EQUALS_PARTIAL = `(?<equals>${YEAR_EQUALS_VALUE}${SPACE_OR_END_POS_LOOKAHEAD})`
const YEAR_RANGE_PARTIAL = `(?<range>${YEAR_RANGE_VALUE}${SPACE_OR_END_POS_LOOKAHEAD})`
const YEAR_FRONT_OPERATOR_PARTIAL = `(?<frontOp>${YEAR_FRONT_OPERATOR_VALUE}${SPACE_OR_END_POS_LOOKAHEAD})`
const YEAR_BACK_OPERATOR_PARTIAL = `(?<backOp>${YEAR_BACK_OPERATOR_VALUE}${SPACE_OR_END_POS_LOOKAHEAD})`

const NUMBERS_EQUALS_VALUE = `(?<equalsValue>${NUMBERS})`
const NUMBERS_RANGE_VALUE = `(?<rangeLowValue>${NUMBERS})-(?<rangeHighValue>${NUMBERS})`
const NUMBERS_FRONT_OPERATOR_VALUE = `(?<frontOperator>${FRONT_OPERATOR})(?<frontOperatorValue>${NUMBERS})`
const NUMBERS_BACK_OPERATOR_VALUE = `(?<backOperatorValue>${NUMBERS})(?<backOperator>${BACK_OPERATOR})`
const NUMBERS_EQUALS_PARTIAL = `(?<equals>${NUMBERS_EQUALS_VALUE}${SPACE_OR_END_POS_LOOKAHEAD})`
const NUMBERS_RANGE_PARTIAL = `(?<range>${NUMBERS_RANGE_VALUE}${SPACE_OR_END_POS_LOOKAHEAD})`
const NUMBERS_FRONT_OPERATOR_PARTIAL = `(?<frontOp>${NUMBERS_FRONT_OPERATOR_VALUE}${SPACE_OR_END_POS_LOOKAHEAD})`
const NUMBERS_BACK_OPERATOR_PARTIAL = `(?<backOp>${NUMBERS_BACK_OPERATOR_VALUE}${SPACE_OR_END_POS_LOOKAHEAD})`

const SIMPLE_ALPHANUMERIC_VALUE = `(?<alphanumValue>${ALPHANUMERIC_DASH})`
const SIMPLE_QUOTED_VALUE = `(?<quotedValue>${QUOTED})`
const SIMPLE_COMMA_SEPARATED_VALUE = `(?<commaValue>${COMMA_SEPARATED})`
const SIMPLE_ALPHANUMERIC_PARTIAL = `(?<alphanum>${SIMPLE_ALPHANUMERIC_VALUE}${SPACE_OR_END_POS_LOOKAHEAD})`
const SIMPLE_QUOTED_PARTIAL = `(?<quoted>${SIMPLE_QUOTED_VALUE}${SPACE_OR_END_POS_LOOKAHEAD})`
const SIMPLE_COMMA_SEPARATED_PARTIAL = `(?<comma>${SIMPLE_COMMA_SEPARATED_VALUE}${SPACE_OR_END_POS_LOOKAHEAD})`

// export const OLD_HASWORDS_REGEX_PATTERN = `(?:${LEADING_SPACE})(")?(?:[A-Z]+|\\s)*(?=\\s[A-Z]+:|\\s[0-9<>]|$)`
const HASWORDS_VALUE = '(?<hasWordsValue>(")?(?:[A-Z]+|\\s)*(?=\\s[A-Z]+:|\\s[0-9<>]|$))'
export const HASWORDS_PARTIAL = `(?<hasWords>)${HASWORDS_VALUE}`

// Input Options
const ANY_AQL_OPTIONS = `(?<value>${[SIMPLE_QUOTED_PARTIAL, ANY_PARTIAL].join('|')})`
const YEAR_INPUT_OPTIONS = `(?<value>${[
    YEAR_EQUALS_PARTIAL,
    YEAR_RANGE_PARTIAL,
    YEAR_FRONT_OPERATOR_PARTIAL,
    YEAR_BACK_OPERATOR_PARTIAL,
].join('|')})`
const YEAR_AND_ODO_INPUT_OPTIONS = `(?<value>${[
    NUMBERS_EQUALS_PARTIAL,
    NUMBERS_RANGE_PARTIAL,
    NUMBERS_FRONT_OPERATOR_PARTIAL,
    NUMBERS_BACK_OPERATOR_PARTIAL,
].join('|')})`
const LOW_HIGH_INPUT_OPTIONS = `(?<value>${[
    NUMBERS_EQUALS_PARTIAL,
    NUMBERS_RANGE_PARTIAL,
    NUMBERS_FRONT_OPERATOR_PARTIAL,
    NUMBERS_BACK_OPERATOR_PARTIAL,
].join('|')})`
const ALPHANUMERIC_AQL_OPTIONS = `(?<value>${[
    SIMPLE_QUOTED_PARTIAL, // QUOTES ONLY IN AQL STRING, NOT CARD CONFIG INPUT
    SIMPLE_ALPHANUMERIC_PARTIAL,
    SIMPLE_COMMA_SEPARATED_PARTIAL,
].join('|')})`
export const ALPHANUMERIC_INPUT_OPTIONS = `(?<value>${[
    SIMPLE_ALPHANUMERIC_PARTIAL,
    SIMPLE_COMMA_SEPARATED_PARTIAL,
].join('|')})`
export const NUMBER_OPERATOR_INPUT_OPTIONS = `(?<value>${[
    NUMBERS_EQUALS_PARTIAL,
    NUMBERS_RANGE_PARTIAL,
    NUMBERS_FRONT_OPERATOR_PARTIAL,
    NUMBERS_BACK_OPERATOR_PARTIAL,
].join('|')})`

// AQL Patterns
export const HASWORDS_AQL_PATTERN = `${LEADING_SPACE_NON_CAPTURE}${HASWORDS_PARTIAL}`
export const YEAR_AQL_PATTERN = `${LEADING_SPACE_NON_CAPTURE}${NO_KEY_WITH_COLON}${YEAR_INPUT_OPTIONS}`
export const YEAR_AND_ODO_AQL_PATTERN = `${LEADING_SPACE_NON_CAPTURE}${NO_KEY_WITH_COLON}${YEAR_AND_ODO_INPUT_OPTIONS}`
export const LOW_HIGH_AQL_PATTERN = `${LEADING_SPACE_NON_CAPTURE}${KEY_WITH_COLON}${LOW_HIGH_INPUT_OPTIONS}`
export const ALPHANUMERIC_AQL_PATTERN = `${LEADING_SPACE_NON_CAPTURE}${KEY_WITH_COLON}${ALPHANUMERIC_AQL_OPTIONS}`
export const ALPHANUMERIC_INPUT_PATTERN = `${LEADING_SPACE_NON_CAPTURE}${KEY_WITH_COLON}${ALPHANUMERIC_INPUT_OPTIONS}`
export const NUMBER_OPERATOR_AQL_PATTERN = `${LEADING_SPACE_NON_CAPTURE}${KEY_WITH_COLON}${NUMBER_OPERATOR_INPUT_OPTIONS}`

// Targeted Regex Patterns. Speeds up AQL search by matching a specifc key in a key:value pattern
const targetedKeyWIthColon = (key: string) => `(?<keyWithColon>(?<key>${key})(?<colon>${COLON}))`

export const targetedKeyLowHighFieldPattern = (key: string): string =>
    `${LEADING_SPACE_NON_CAPTURE}${targetedKeyWIthColon(key)}${LOW_HIGH_INPUT_OPTIONS}`
export const targetedKeyNotFieldPattern = (key: string): string =>
    `${LEADING_SPACE_NON_CAPTURE}${targetedKeyWIthColon(key)}${ALPHANUMERIC_AQL_OPTIONS}`
export const targetedAlphanumericFieldPattern = (key: string): string =>
    `${LEADING_SPACE_NON_CAPTURE}${targetedKeyWIthColon(key)}${ALPHANUMERIC_AQL_OPTIONS}`
export const targetedNumberOperatorFieldValuePattern = (key: string): string =>
    `${LEADING_SPACE_NON_CAPTURE}${targetedKeyWIthColon(key)}${NUMBER_OPERATOR_INPUT_OPTIONS}`
export const targetedAnyAqlValuePattern = (key: string): string =>
    `${LEADING_SPACE_NON_CAPTURE}${targetedKeyWIthColon(key)}${ANY_AQL_OPTIONS}`
